import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  dateTimeFacade,
  dateTimeHelper,
  EBookingPageType,
  ecNoOnlyWhiteSpacesValidator,
  EMAIL_EXP,
  getContrastYIQ,
  injectEnvironment,
  LayoutService,
  NON_DIGIT_PATTERN,
  ResponsiveService,
} from '@cf/core';

import { AsyncPipe, NgIf } from '@angular/common';
import { TranslateCutPipe } from '@cf/features/i18n';
import { EventService } from '@cf/features/schedules';
import { SwitchComponent } from '@cf/shared/forms/primitives';
import { ClipboardButtonComponent, LinkComponent } from '@cf/shared/ui/button';
import { CfIconDirective } from '@cf/shared/ui/icons';
import { LoaderContainerComponent } from '@cf/shared/ui/loading';
import { AvatarComponent } from '@cf/shared/ui/logos';
import { TextComponent, TextTruncateComponent } from '@cf/shared/ui/typography';
import {
  AccountService,
  AuthService,
  CfSchemaMeetingInfo,
  EPricingModel,
  ETimezoneSettings,
  IBooking,
  IBookingParams,
  IEventContainer,
  ITimeslots,
  SchemaMeetingInfo,
  TimeFormatEnum,
  UsersService,
} from '@cf/temp/services';
import { NgbModal, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { CookieService } from 'ngx-cookie-service';
import {
  BookingAvailabilityComponent,
  BookingConfirmationComponent,
  BookingFooterComponent,
  BookingPersonalComponent,
  BookingPickerComponent,
  BookingStepsComponent,
} from '../../components';
import { BookingCalendarComponent } from '../../components/booking-calendar/booking-calendar.component';
import { setBookingQuestionFormErrors } from '../../const';
import { BookingPasswordModalComponent } from '../../modals/booking-password/booking-password-modal.component';
import { BookingApiService } from '../../services/booking-api.service';
import { BookingInformationComponent } from '../../shared/booking-information/booking-information.component';
@Component({
  selector: 'cf-booking',
  templateUrl: './booking-page.component.html',
  styleUrls: ['./booking-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TextComponent,
    TextTruncateComponent,
    LinkComponent,
    ClipboardButtonComponent,
    RouterLink,
    NgxTolgeeModule,
    NgIf,
    AsyncPipe,
    FormsModule,
    AvatarComponent,
    BookingInformationComponent,
    LoaderContainerComponent,
    BookingCalendarComponent,
    NgbPopover,
    BookingPickerComponent,
    BookingFooterComponent,
    BookingConfirmationComponent,
    BookingPersonalComponent,
    BookingAvailabilityComponent,
    BookingStepsComponent,
    CfIconDirective,
    TranslateCutPipe,
    SwitchComponent,
  ],
})
export class BookingPageComponent implements OnInit, OnDestroy {
  @ViewChild(BookingCalendarComponent)
  bookingCalendarComponent!: BookingCalendarComponent;

  @Input() isPreview = false;
  @Input() previewMode: 'desktop' | 'mobile' = 'desktop';

  params!: IBookingParams;

  booking!: CfSchemaMeetingInfo;
  previewPassword?: string;
  timeslots!: ITimeslots[];
  events: IEventContainer[] = [];
  form!: FormGroup;

  selectedSlot!: ITimeslots;
  selectedTime!: string;
  @Input() selectedDuration!: string;
  participantsAmount!: number;
  isTouch!: boolean;
  troubleshoot = false;
  bookingLink!: string;
  hasFreeSlots = true;

  readonly EMeetingType = EBookingPageType;
  readonly EPricingModel = EPricingModel;
  readonly getContrastYIQ = getContrastYIQ;

  isTouch$ = this.responsiveService.isTouch$;

  timezone: string = this.accountService.timezone;
  disableTimezone = false;
  dateRange = {
    from_date: dateTimeFacade.startOf('month').toSQLDate(),
    to_date: dateTimeFacade.endOf('month').toSQLDate(),
  };

  submitted = false;

  @Output() emitDateRange: EventEmitter<{
    from_date: string;
    to_date: string;
  }> = new EventEmitter();
  @Output() emitDuration = new EventEmitter();

  private destroyRef = inject(DestroyRef);

  environment = injectEnvironment();

  readonly ETimezoneSettings = ETimezoneSettings;

  eventAddedToCalendar: boolean | undefined;

  get hasActualLink() {
    const url = this.url;
    return url.includes('meeting/create');
  }

  constructor(
    public readonly accountService: AccountService,
    public readonly authService: AuthService,
    public readonly route: ActivatedRoute,
    public readonly cd: ChangeDetectorRef,

    private readonly bookingApiService: BookingApiService,
    private readonly eventService: EventService,
    private readonly responsiveService: ResponsiveService,
    private readonly layoutService: LayoutService,
    private readonly modalService: NgbModal,
    private readonly fb: FormBuilder,
    private readonly router: Router,

    private readonly usersService: UsersService,
    private readonly cookieService: CookieService,
  ) {
    this.params = { ...this.route.snapshot.params, color: '#3158e3' } as any;
  }

  get preview() {
    return this.route.snapshot.queryParams['preview'] === 'true';
  }

  get editable() {
    return this.route.snapshot.queryParams['editable'] === 'true';
  }

  get templateID() {
    return this.route.snapshot.queryParams['templateID'];
  }

  get url() {
    return this.router.url;
  }

  get stepIndex(): number {
    if (!this.selectedSlot) {
      return 0;
    } else if (!this.selectedTime) {
      return 1;
    } else {
      return 2;
    }
  }

  get selectedDate() {
    if (this.selectedSlot) {
      return dateTimeHelper.getDateFromISO(this.selectedSlot.date).toJSDate();
    }

    return null;
  }

  get bookingTranslateObj() {
    return {
      total_price: '',
      price: this.booking.price,
      currency_code: this.booking.currency_code,
    };
  }

  get pathAsDuratoin() {
    return (
      this.params.path?.includes('min') &&
      !isNaN(+this.params.path?.split('min')[0]) &&
      this.params.path?.split('min').length === 2 &&
      !this.params.duration
    );
  }

  get paramsPath() {
    return this.pathAsDuratoin ? '' : this.params.path;
  }

  get redirection_url_ms_booking() {
    return this.cookieService.get('redirection_url_ms_booking');
  }

  ngOnInit() {
    if (this.redirection_url_ms_booking) {
      this.cookieService.delete('redirection_url_ms_booking');
    }
    if (this.preview) {
      this.bookingLink = window.location.href.split('?')[0];
    }
    if (!this.isPreview) {
      this.layoutService.hideHeader.next(true);
    }

    if (this.params.username) {
      this.bookingApiService
        .booking(this.paramsPath, this.params.username)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((b) => {
          const durationParam = this.pathAsDuratoin
            ? this.params.path.replace(NON_DIGIT_PATTERN, '')
            : this.params.duration?.replace(NON_DIGIT_PATTERN, '');
          const duration = !isNaN(+durationParam)
            ? durationParam
            : b.selected_duration
              ? dateTimeFacade.timeToDuration(b.selected_duration).toString()
              : undefined;

          if (!duration) {
            void this.router.navigate(['/error/404']);

            return;
          }

          if (b.is_expired) {
            void this.router.navigate(['/error']);

            return;
          }

          this.selectedDuration = duration;
          this.booking = b;
          // this.booking.first_availability = dateTimeHelper.dt.now().toSQLDate();
          this.handleDuration(b);
          this.handleTimezone(b);
          this.handleBPPassword();
        });
    }

    this.isTouch$.subscribe((res) => {
      this.isTouch = res;
    });

    this.authService.isAuthorized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      if (res && this.selectedSlot) {
        this.getEvents(this.selectedSlot);
      }
    });

    this.accountService.account$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((acc) => {
      if (!this.isPreview && this.form) {
        this.form.patchValue(acc);
      }
    });

    this.prepareForm();
  }

  ngOnDestroy() {
    this.layoutService.hideHeader.next(false);
  }

  applyDate(date: { from_date: string; to_date: string }) {
    this.dateRange = date;
    this.getTimeslots(this.timezone, this.selectedDuration);
    this.emitDateRange.emit(this.dateRange);
  }

  durationChanged(duration: string) {
    this.getTimeslots(this.timezone, duration);
    this.selectedDuration = duration;
    this.emitDuration.emit(duration);
  }

  getTimeslots(timezone: string, duration = this.selectedDuration) {
    this.timezone = timezone;

    if (this.selectedSlot) {
      this.getEvents(this.selectedSlot);
    }

    if (this.params.username) {
      this.bookingApiService
        .hasFreeSlots(this.paramsPath, this.params.username, {
          duration: dateTimeFacade.decimalToDuration({ minute: 5 }),
        })
        .subscribe((res) => {
          this.hasFreeSlots = res;
          this.cd.detectChanges();
        });

      this.bookingApiService
        .timeslots(this.paramsPath, this.params.username, {
          timezone,
          ...this.dateRange,
          duration: dateTimeFacade.decimalToDuration({ minute: +duration }),
        })
        .subscribe((res) => {
          this.timeslots = res;
          if (this.selectedSlot) {
            const slot = res.find((s) => s.date === this.selectedSlot.date);

            if (slot) {
              this.selectedSlot = slot as any;
            } else {
              this.resetSlot();
            }
          } else {
            this.resetSlot();
          }

          this.cd.detectChanges();
        });
    }
  }

  confirm(data: { time: string; duration: string }) {
    this.selectedTime = dateTimeHelper.getTime(
      data.time,
      this.booking.time_format === TimeFormatEnum['12h'] ? 12 : 24,
      true,
      undefined,
      true,
    );
    this.selectedDuration = data.duration;
  }

  submit(formValue: any, booking?: CfSchemaMeetingInfo) {
    const dto = {
      timezone: this.timezone,
      date: this.selectedSlot.date,
      time: dateTimeHelper.getTimeFromFormat(this.selectedTime, 24),
      duration: dateTimeFacade.durationToDate(+this.selectedDuration),
      booker: { ...formValue },
      additional_guests: formValue.additional_guests,
      password: formValue.password,
      payment_method_id: formValue.payment_method_id,
      questions: formValue.questions,
    };

    if (!formValue.payment_method_id) {
      delete dto.payment_method_id;
    }

    if (!this.booking.has_password) {
      delete dto.password;
    }

    if (booking) {
      this.participantsAmount =
        (this.booking.members_amount ?? 0) + ((dto.additional_guests as string[])?.length ?? 0) + 1;
    }

    if (this.accountService.account?.email) {
      dto.booker = null;
    }

    if (this.isPreview || this.preview) {
      this.submitted = true;
      return;
    }

    this.bookingApiService.book(this.paramsPath, this.params.username, dto).subscribe({
      next: (res) => {
        this.eventAddedToCalendar = res.event_added_to_calendar;
        this.submitted = true;
      },
      error: (err) => {
        setBookingQuestionFormErrors(this.form, err, dto.questions);
      },
    });
  }

  refresh() {
    this.resetSlot();
    this.selectedTime = undefined as any;
    this.selectedDuration = undefined as any;
    this.submitted = false;
  }

  stepChange(index: number) {
    if (index === 0) {
      this.resetSlot();
      this.selectedTime = undefined as any;
      this.selectedDuration = undefined as any;
    }
    if (index === 1) {
      this.selectedTime = undefined as any;
      this.selectedDuration = undefined as any;
    }
  }

  back() {
    this.selectedTime = undefined as any;
  }

  getEvents(slot: ITimeslots) {
    if (slot) {
      this.selectedSlot = slot;
    }

    if (this.isPreview || this.preview) {
      return;
    }

    const dto = {
      from_date: this.selectedSlot.date ?? this.dateRange.from_date,
      to_date: this.selectedSlot.date ?? this.dateRange.from_date,
      timezone: this.timezone,
    };

    if (this.authService.isAuthorized()) {
      // this.usersService
      //   .getCalendarSettings()
      //   .pipe(
      //     switchMap((calendars) => {
      //       return this.eventService.events({
      //         ...dto,
      //         // calendars: calendars.availability_calendars,
      //       });
      //     }),
      //     takeUntilDestroyed(this.destroyRef),
      //   )
      this.eventService
        .events({
          ...dto,
          // calendars: calendars.availability_calendars,
        })
        .subscribe((res) => {
          this.events = res ?? [];
          this.cd.detectChanges();
        });
    }
  }

  focusTime(e: IEventContainer[]) {
    this.events = [...e];
  }

  public scheduleAnother() {
    if (this.isPreview || this.preview) {
      this.refresh();
    } else {
      window.location.reload();
    }
  }

  public handleTimezone(booking: Pick<IBooking, 'timezone_setting' | 'organizer_timezone'>) {
    if (!booking.timezone_setting) {
      this.timezone = booking.organizer_timezone;
      this.disableTimezone = true;
    } else if (booking.timezone_setting === ETimezoneSettings.AUTO_DETECT) {
      this.timezone = this.accountService.localTimezone;
      this.disableTimezone = false;
    } else if (booking.timezone_setting === ETimezoneSettings.LOCK) {
      this.timezone = booking.organizer_timezone;
      this.disableTimezone = true;
    }

    if (this.isPreview) {
      this.timezone = booking.organizer_timezone;
    }

    this.getTimeslots(this.timezone);
    this.cd.detectChanges();
  }

  public setFreeSlot() {
    this.selectedSlot = this.timeslots?.find((slot) => {
      return !!slot.time_slots?.length && slot.has_available_slots;
    }) as ITimeslots;

    if (!this.selectedSlot) {
      this.resetSlot();
    }
  }

  public resetSlot() {
    if (this.isTouch) {
      this.selectedSlot = undefined as any;
    } else if (this.timeslots) {
      if (!this.timeslots.length) {
        this.selectedSlot = {
          ...this.selectedSlot,
          free_slots: [],
          time_slots: [],
        };
        return;
      }

      this.selectedSlot = this.timeslots.find((slot) => {
        if (slot.time_slots) {
          return !!slot.time_slots?.length || slot.has_daily_booking_limit_reached_slots;
        } else {
          return !!slot.free_slots?.length;
        }
      }) as any;
    }
  }

  public handleBPPassword(has_password = false) {
    this.modalService.dismissAll();
    if (this.booking.has_password || has_password) {
      const modal = this.modalService.open(BookingPasswordModalComponent, {
        container: 'cf-booking',
        size: 'small',
        windowClass: 'position-absolute',
        backdropClass: 'position-absolute',
      });

      modal.componentInstance.path = this.paramsPath;
      modal.componentInstance.username = this.params.username;
      modal.componentInstance.password = this.previewPassword;

      modal.closed.subscribe((password) => {
        this.form.controls['password'].patchValue(password);
      });

      document.body.style.overflow = 'auto';
    }
  }

  private handleDuration(booking: SchemaMeetingInfo) {
    const d = dateTimeFacade.durationToDate(+this.selectedDuration);

    if (!booking.durations.includes(d)) {
      void this.router.navigate(['/error/404']);
    }
  }

  private prepareForm(): void {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(150), ecNoOnlyWhiteSpacesValidator]],
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          Validators.pattern(EMAIL_EXP),
          Validators.maxLength(150),
        ],
      ],
      additional_guests: [[]],
      password: [''],
      payment_method_id: [],
      questions: this.fb.array([]),
    });
  }

  loginRedirect(url: string): void {
    if (this.isPreview || this.preview) {
      return;
    }
    window.open(url, '_self');
  }
}
